import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { auth } from "../../firebase";
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged } from "firebase/auth";
import { AuthContext } from "../App";

const Login: React.FC = () => {
  const authContext = useContext(AuthContext);
  const setIsAuthenticated = authContext?.setIsAuthenticated;

  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [notificationsAccepted, setNotificationsAccepted] = useState(false);
  const navigate = useNavigate();

  const backendURL = "https://backend-app.politeisland-baa0cb3d.eastus.azurecontainerapps.io";

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (setIsAuthenticated && (user || localStorage.getItem("isAuthenticated") === "true")) {
        setIsAuthenticated(true);
        navigate("/", { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate, setIsAuthenticated]);

  const handlePhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10));
  };

  const handleOtpInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
  };

  const handleSendOtp = async () => {
    if (!termsAccepted) {
      setError("You must accept the Terms & Privacy Policy to proceed.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    if (phoneNumber.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${backendURL}/send-otp`, { phone: phoneNumber });
      if (response.data.success) {
        setOtpSent(true);
        setSuccessMessage("OTP sent successfully!");
      } else {
        setError(response.data.error || "Failed to send OTP.");
      }
    } catch {
      setError("Error sending OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    setError("");
    setSuccessMessage("");

    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${backendURL}/verify-otp`, { phone: phoneNumber, otp });

      if (response.data.success) {
        setSuccessMessage("OTP verified successfully!");
        localStorage.setItem("isAuthenticated", "true");
        if (setIsAuthenticated) setIsAuthenticated(true);
        navigate("/", { replace: true });
      } else {
        setError(response.data.error || "Wrong OTP! Please try again.");
      }
    } catch {
      setError("Server error verifying OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!termsAccepted) {
      setError("You must accept the Terms & Privacy Policy to proceed.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      localStorage.setItem("isAuthenticated", "true");
      if (setIsAuthenticated) setIsAuthenticated(true);
      setSuccessMessage("✅ Logged in successfully with Google!");
      navigate("/", { replace: true });
    } catch {
      setError("❌ Google Sign-In failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-white shadow-lg rounded-lg p-8 w-96">
        <div className="flex justify-center mb-4">
          <img src="/imgs/Duit_Logo_1.PNG" alt="Duit Digital Logo" className="w-50 h-20" />
        </div>

        <h2 className="text-2xl font-bold text-teal-600 mb-6 text-center">Login</h2>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-600">Phone Number</label>
          <div className="flex items-center border rounded-md border-gray-300 px-3 py-2">
            <span className="text-gray-500 mr-2">+91</span>
            <input
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneInputChange}
              className="w-full outline-none"
              placeholder="Enter 10-digit number"
              maxLength={10}
            />
          </div>
        </div>

        {otpSent && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600">Enter OTP</label>
            <input
              type="text"
              value={otp}
              onChange={handleOtpInputChange}
              className="w-full px-3 py-2 border rounded-md border-gray-300"
              placeholder="Enter OTP"
              maxLength={6}
            />
          </div>
        )}

        {/* ✅ Checkboxes */}
        <div className="mb-2 flex items-center">
          <input type="checkbox" id="terms" checked={termsAccepted} onChange={() => setTermsAccepted(!termsAccepted)} className="mr-2" />
          <label htmlFor="terms" className="text-sm text-gray-600">
            I accept the{" "}
            <a href="/terms" target="_blank" className="text-teal-600 underline">
              Terms
            </a>{" "}
            &{" "}
            <a href="/privacy-policy" target="_blank" className="text-teal-600 underline">
              Privacy Policy
            </a>.
          </label>
        </div>

        <div className="mb-4 flex items-center">
          <input type="checkbox" id="notifications" checked={notificationsAccepted} onChange={() => setNotificationsAccepted(!notificationsAccepted)} className="mr-2" />
          <label htmlFor="notifications" className="text-sm text-gray-600">
            I authorize notifications via SMS/WhatsApp/Email and RCS.
          </label>
        </div>

        <button onClick={handleSendOtp} disabled={!termsAccepted || loading} className={`w-full py-2 rounded-md mb-4 ${!termsAccepted ? "bg-gray-300" : "bg-teal-600 text-white"}`}>
          {loading ? "Sending OTP..." : "Send OTP"}
        </button>

        {otpSent && (
          <button onClick={handleVerifyOtp} disabled={loading} className="w-full bg-green-600 text-white py-2 rounded-md mb-4">
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        )}

        <div className="flex justify-center w-full">
          <div
            onClick={handleGoogleSignIn}
            className={`w-full py-2 rounded-md mb-4 flex justify-center items-center border ${
              !termsAccepted ? "bg-gray-300 cursor-not-allowed" : "bg-white hover:bg-gray-100 cursor-pointer"
            }`}
            style={{ pointerEvents: !termsAccepted ? "none" : "auto" }}
          >
            <img src="https://developers.google.com/identity/images/btn_google_signin_light_normal_web.png" alt="Sign in with Google" className="h-10" />
          </div>
        </div>

        {/* ✅ Success & Error Message */}
        {error && <p className="text-red-600 text-sm text-center mt-2">{error}</p>}
        {successMessage && <p className="text-green-600 text-sm text-center mt-2">{successMessage}</p>}
      </div>
    </div>
  );
};

export default Login;
